import { useMemo, useState } from 'react';

import { classNames } from '@/utils';

import type { DMConversationMessage } from '@/components/pages/conversations/SocialInbox/types';

import { SUPPORTED_ATTACHMENT_TYPES } from '@/components/pages/conversations/SocialInbox/const';

import Icon from '@/components/base/Icon';
import { MediaOverlay } from '@/components/composite/MediaOverlay';
import { ImageMedia, VideoMedia } from '@/components/pages/conversations/SocialInbox/models';

import { UnsupportedMessage } from './UnsupportedMessage';

interface MessageMediaProps {
    attachment: DMConversationMessage['attachments'][0];
    isSent?: boolean;
}

const MAX_WIDTH = 256;
const MAX_HEIGHT = 320;
const MIN_SIZE = 150;

export const MessageMedia = ({ attachment, isSent }: MessageMediaProps) => {
    const [imgError, setImgError] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    const isSupported = SUPPORTED_ATTACHMENT_TYPES.includes(attachment.type);
    const isVideo = attachment.type === 'video';
    const media = isVideo ? new VideoMedia(attachment) : new ImageMedia(attachment);

    const dimensions = useMemo(() => {
        const rawWidth = parseInt(attachment.width || '0', 10);
        const rawHeight = parseInt(attachment.height || '0', 10);

        // If no dimensions, use default square
        if (!rawWidth || !rawHeight) {
            return { width: MAX_WIDTH, height: MAX_WIDTH };
        }

        const aspectRatio = rawWidth / rawHeight;

        let width = rawWidth;
        let height = rawHeight;

        // Scale down if too large
        if (width > MAX_WIDTH) {
            width = MAX_WIDTH;
            height = Math.round(width / aspectRatio);
        }
        if (height > MAX_HEIGHT) {
            height = MAX_HEIGHT;
            width = Math.round(height * aspectRatio);
        }

        // Ensure minimum size - scale up if either dimension is below MIN_SIZE
        if (width < MIN_SIZE || height < MIN_SIZE) {
            const scaleFactor = Math.max(MIN_SIZE / width, MIN_SIZE / height);
            width = Math.round(width * scaleFactor);
            height = Math.round(height * scaleFactor);
        }

        return { width, height };
    }, [attachment.width, attachment.height]);

    if (!isSupported) {
        return <UnsupportedMessage isSent={isSent} />;
    }

    return (
        <div className={classNames('mb-2 flex', isSent ? 'justify-end' : 'justify-start')}>
            <div
                className="dark:bg-icoDarkMode-gray relative overflow-hidden rounded-lg bg-icoGray-100"
                style={{ width: dimensions.width, height: dimensions.height }}>
                <MediaOverlay media={[media]} currentIndex={0} className="h-full w-full">
                    {isVideo ? (
                        <>
                            <video
                                src={attachment.url}
                                poster={attachment.preview_url}
                                className="h-full w-full object-cover"
                                preload="metadata"
                                onLoadedData={() => setIsLoading(false)}
                            />
                            <div className="absolute inset-0 flex items-center justify-center bg-icoBlack/30">
                                <Icon icon="play" className="text-3xl text-white" />
                            </div>
                        </>
                    ) : (
                        <>
                            {isLoading && (
                                <div className="bg-icoGray-200 dark:bg-icoDarkMode-gray absolute inset-0 animate-pulse" />
                            )}
                            {imgError ? (
                                <div className="flex h-full w-full items-center justify-center">
                                    <Icon icon="albums" className="text-4xl text-icoGray-400" />
                                </div>
                            ) : (
                                <img
                                    src={attachment.preview_url || attachment.url}
                                    alt=""
                                    className="h-full w-full object-cover"
                                    onLoad={() => setIsLoading(false)}
                                    onError={() => {
                                        setIsLoading(false);
                                        setImgError(true);
                                    }}
                                />
                            )}
                        </>
                    )}
                </MediaOverlay>
            </div>
        </div>
    );
};
